import { useEffect } from 'react';

import { setToken } from '@/lib/authActions';
import { store } from '@/store';
import { isTauri } from '@/tauri/utils';
import { readPersistedAuthSlice } from '@/utils/auth/readPersistedAuthSlice';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { authTokenSyncedAtom } from '@heidi/auth/state';
import { GET_USER } from '@heidi/workspace/contracts';
import { getDefaultStore } from 'jotai';

const defaultJotaiStore = getDefaultStore();

const PERSIST_ROOT_KEY = 'persist:root';

type PersistedAuth = {
  token?: string;
};

/**
 * Lazy-imported to dodge the static circular through
 * `getAccessToken → @/store → models/authModel → @/lib/auth`. The hook
 * runs in async / event-handler contexts where awaiting the import is
 * fine.
 */
async function clearCacheAfterPropagatedLogout(): Promise<void> {
  const { clearAuthCache } = await import('@/lib/auth');
  clearAuthCache({ reason: 'logout' });
}

export const syncPersistedAuthToStore = (): void => {
  const next = readPersistedAuthSlice<PersistedAuth>();
  const state = store.getState().auth;
  if (!next) {
    if (state.token) {
      store.dispatch.auth.clear();
      // Propagate the cache clear so the auxiliary Tauri window's
      // cache atoms don't keep handing out the stale token after the
      // main window has logged out.
      void clearCacheAfterPropagatedLogout();
      defaultJotaiStore.set(authTokenSyncedAtom, false);
    }
    return;
  }

  const nextToken = next.token;
  if (nextToken !== state.token) {
    if (nextToken) {
      void setToken(nextToken);
    } else {
      store.dispatch.auth.clear();
      void clearCacheAfterPropagatedLogout();
      defaultJotaiStore.set(authTokenSyncedAtom, false);
      return;
    }
  }

  if (nextToken) {
    const hasPersistedUser = !!reactQueryClient.getQueryData([GET_USER]);
    defaultJotaiStore.set(authTokenSyncedAtom, hasPersistedUser);
  }
};

/**
 * Mirror `auth.token` from main-window writes to `persist:root` into this
 * Tauri auxiliary window's Redux store.
 *
 * The region is not mirrored: it is no longer persisted, and this reader goes
 * at raw `localStorage` — bypassing every redux-persist transform — so a
 * mirror leg here would fire exactly once, off a legacy blob on the upgrade
 * launch, and never again. `getRegion()` derives the region from the mirrored
 * token instead.
 *
 * Gated on `isTauri()` because the EHR integration launch routes also use
 * `MinimalProviders` but run as standalone web flows — they must not see
 * cross-window auth mutations.
 */
export function useAuthTokenStorageSync(): void {
  useEffect(() => {
    if (!isTauri()) return;

    syncPersistedAuthToStore();

    const handler = (event: StorageEvent) => {
      if (event.key !== PERSIST_ROOT_KEY) return;

      syncPersistedAuthToStore();
    };

    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);
}
